import Payment from "payment"

export function clearNumber(value = "") {
  return value?.replace(/\D+/g, "")
}

export function clearAlphaNumeric(value = "") {
  return value.replace(/[^a-zA-Z0-9]/g, "");
}

export function generateSSNumber(value = "") {
  const clearValue = clearNumber(value)?.slice(0, 9)

  if (clearValue?.length >= 6) {
    return `${clearValue?.slice(0, 3)}-${clearValue?.slice(3, 5)}-${clearValue?.slice(5, 9)}`
  }

  return clearValue
}

// generateSSNumber strips the asterisks off a masked SSN, leaving four bare digits that look like a
// real entry and would overwrite the stored number on save.
export function ssnForPrefill(value) {
  if (!value) return ""
  if (typeof value === "string" && value.includes("*")) return ""

  const digits = clearNumber(String(value))
  if (digits.length !== 9) return ""

  return generateSSNumber(String(value))
}

// vlg-api utils/ssn.js maskSSN emits '*****' + last four for affiliate roles, so that shape means
// the number is on file and withheld, not that the record is empty.
export function hasSsnOnFile(value) {
  if (!value) return false

  const raw = String(value)
  if (/^\*+\d{4}$/.test(raw)) return true

  return clearNumber(raw).length === 9
}

// A blank SSN field means "cleared on purpose" to someone who could read the number and "never shown
// to me" to someone who could not. Only the first is an edit; sending the second erases the record.
export function omitSsnUpdate(value, wasWithheld) {
  return Boolean(wasWithheld) && !clearNumber(value ? String(value) : "")
}

export function phoneNumber(value = "") {
  const clearValue = clearNumber(value)

  if (clearValue?.length >= 7) {
    return `(${clearValue?.slice(0, 3)}) ${clearValue?.slice(3, 6)}-${clearValue?.slice(6, 10)}`
  }

  return clearValue
}


const PROVIDER_TO_ISSUER = {
  "Visa": "visa",
  "Mastercard": "mastercard",
  "American Express": "amex",
  "Discover": "discover",
}

const isAmexProvider = (provider) => provider === "American Express"

const resolveIssuer = (provider, value) => {
  if (provider && PROVIDER_TO_ISSUER[provider]) {
    return PROVIDER_TO_ISSUER[provider]
  }
  return Payment.fns.cardType(value)
}

export function formatCreditCardNumber(value, provider = null) {
  if (!value) {
    return value
  }

  const issuer = resolveIssuer(provider, value)
  let clearValue = clearNumber(value)
  let nextValue

  const maxLength = issuer === "amex" ? 15 : 16
  if (clearValue?.length > maxLength) {
    clearValue = clearValue?.slice(0, maxLength)
  }
  switch (issuer) {
    case "amex":
      nextValue = `${clearValue?.slice(0, 4)} ${clearValue?.slice(
        4,
        10
      )} ${clearValue?.slice(10, 15)}`
      break
    case "dinersclub":
      nextValue = `${clearValue?.slice(0, 4)} ${clearValue?.slice(
        4,
        10
      )} ${clearValue?.slice(10, 14)}`
      break
    default:
      nextValue = `${clearValue?.slice(0, 4)} ${clearValue?.slice(
        4,
        8
      )} ${clearValue?.slice(8, 12)} ${clearValue?.slice(12, 19)}`
      break
  }

  return nextValue.trim()
}

export function formatCVC(value, allValues = {}) {
  const clearValue = clearNumber(value)
  let maxLength = 3

  if (allValues.provider && PROVIDER_TO_ISSUER[allValues.provider]) {
    maxLength = isAmexProvider(allValues.provider) ? 4 : 3
  } else if (allValues.number) {
    const issuer = Payment.fns.cardType(allValues.number)
    maxLength = issuer === "amex" ? 4 : 3
  }

  return clearValue?.slice(0, maxLength)
}

export function formatExpirationDate(value) {
  const clearValue = clearNumber(value)

  if (clearValue?.length >= 3) {
    return `${clearValue?.slice(0, 2)}/${clearValue?.slice(2, 4)}`
  }

  return clearValue
}

export function formatFormData(data) {
  return Object.keys(data).map(d => `${d}: ${data[d]}`)
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return 'N/A'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount)
}

const PROVIDER_RULES = {
  "Visa": { prefixes: ["4"], length: 16 },
  "Mastercard": { prefixes: ["51", "52", "53", "54", "55", "22", "23", "24", "25", "26", "27"], length: 16 },
  "American Express": { prefixes: ["34", "37"], length: 15 },
  "Discover": { prefixes: ["6011", "65", "644", "645", "646", "647", "648", "649"], length: 16 },
}

export function validateDebitCardNumber(value, provider = null) {
  if (!value) {
    return { isValid: false, error: "Card Number required", errorType: "empty" }
  }

  const digits = clearNumber(value)

  if (provider && PROVIDER_RULES[provider]) {
    const rules = PROVIDER_RULES[provider]
    const matchesPrefix = rules.prefixes.some(p => digits.startsWith(p))
    if (!matchesPrefix) {
      return {
        isValid: false,
        error: `${provider} cards must start with ${rules.prefixes.join(" or ")}`,
        errorType: "invalidPrefix"
      }
    }
    if (digits.length !== rules.length) {
      return {
        isValid: false,
        error: `${provider} cards must have ${rules.length} digits`,
        errorType: "tooShort"
      }
    }
    return { isValid: true, error: null, errorType: null }
  }

  const firstDigit = digits.charAt(0)
  const validFirstDigits = ['3', '4', '5', '6']
  if (!validFirstDigits.includes(firstDigit)) {
    return {
      isValid: false,
      error: "Invalid card number",
      errorType: "invalidPrefix"
    }
  }

  const minLength = firstDigit === '3' ? 15 : 16
  if (digits.length < minLength) {
    return {
      isValid: false,
      error: `Card number must have at least ${minLength} digits`,
      errorType: "tooShort"
    }
  }

  return { isValid: true, error: null, errorType: null }
}
